/**
 * OptionPickerDialog Component
 *
 * Modal dialog with visual grid selection for photostudio options.
 * Features 4-column grid with hover preview overlay.
 *
 * Design specs:
 * - Dialog: #1A1A1A fill, 8px corner radius
 * - Grid: 4 columns, 16px gap
 * - Hover preview: Enlarged card appears as overlay near hovered item
 * - Header: Title + option count (e.g., "// 25 OPTIONS AVAILABLE")
 */

import { useReducer, useMemo, useCallback, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { X, Search } from 'lucide-react';

import { OptionCard } from './OptionCard';
import type { IOptionPickerDialogProps, IPhotostudioOption } from '@/@types/photostudio';
import { getOptionThumbnailPath, getPlaceholderThumbnailPath } from '@/@types/photostudio';

interface IHoverState {
  option: IPhotostudioOption;
  rect: DOMRect;
  dialogRect: DOMRect;
}

type PickerState = {
  hoverState: IHoverState | null;
  previewImageError: boolean;
  searchQuery: string;
};

type PickerAction =
  | { type: 'SET_HOVER'; payload: IHoverState | null }
  | { type: 'SET_PREVIEW_ERROR'; payload: boolean }
  | { type: 'SET_SEARCH_QUERY'; payload: string }
  | { type: 'HOVER_START'; payload: IHoverState }
  | { type: 'RESET' };

const initialPickerState: PickerState = {
  hoverState: null,
  previewImageError: false,
  searchQuery: '',
};

function pickerReducer(state: PickerState, action: PickerAction): PickerState {
  switch (action.type) {
    case 'SET_HOVER':
      return { ...state, hoverState: action.payload };
    case 'SET_PREVIEW_ERROR':
      return { ...state, previewImageError: action.payload };
    case 'SET_SEARCH_QUERY':
      return { ...state, searchQuery: action.payload };
    case 'HOVER_START':
      return { ...state, hoverState: action.payload, previewImageError: false };
    case 'RESET':
      return initialPickerState;
  }
}

export function OptionPickerDialog({
  open,
  onOpenChange,
  title,
  options,
  selectedValue,
  onSelect,
  category,
}: IOptionPickerDialogProps) {
  const [pickerState, dispatch] = useReducer(pickerReducer, initialPickerState);
  const { hoverState, previewImageError, searchQuery } = pickerState;
  const dialogRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      dispatch({ type: 'RESET' });
    }
  }, [open]);

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter(option => option.label.toLowerCase().includes(query));
  }, [options, searchQuery]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleSelect = useCallback(
    (value: string) => {
      onSelect(value);
      onOpenChange(false);
    },
    [onSelect, onOpenChange]
  );

  const handleHover = useCallback((option: IPhotostudioOption, element: HTMLElement) => {
    // Cancel any pending hover end
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    const rect = element.getBoundingClientRect();
    // Read dialog ref in the event handler (not during render) to avoid react-hooks/refs violation
    const dialogRect = dialogRef.current?.getBoundingClientRect() ?? rect;
    dispatch({ type: 'HOVER_START', payload: { option, rect, dialogRect } });
  }, []);

  const handleHoverEnd = useCallback(() => {
    // Delay clearing hover state to allow mouse to move to preview
    hoverTimeoutRef.current = setTimeout(() => {
      dispatch({ type: 'SET_HOVER', payload: null });
    }, 100);
  }, []);

  const handlePreviewMouseEnter = useCallback(() => {
    // Cancel the hover end timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  const handlePreviewMouseLeave = useCallback(() => {
    dispatch({ type: 'SET_HOVER', payload: null });
  }, []);

  // Forward wheel events from preview to scroll container
  const handlePreviewWheel = useCallback((e: React.WheelEvent) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop += e.deltaY;
      // Clear hover state when scrolling to update preview position
      dispatch({ type: 'SET_HOVER', payload: null });
    }
  }, []);

  // Get preview thumbnail path
  const previewThumbnailPath = hoverState
    ? previewImageError
      ? getPlaceholderThumbnailPath(category)
      : getOptionThumbnailPath(category, hoverState.option.value)
    : null;

  // Calculate preview position - centered on top of the hovered thumbnail, constrained to dialog.
  // dialogRect is captured in handleHover (event handler) to avoid reading ref during render.
  const previewStyle = useMemo(() => {
    if (!hoverState) return {};

    const dialogRect = hoverState.dialogRect;
    const cardRect = hoverState.rect;

    // Preview is larger than the card, centered on it
    const scale = 1.5;
    const previewWidth = cardRect.width * scale;
    const previewHeight = cardRect.height * scale;
    const padding = 16;

    // Center the preview on the card
    let left = cardRect.left - dialogRect.left - (previewWidth - cardRect.width) / 2;
    let top = cardRect.top - dialogRect.top - (previewHeight - cardRect.height) / 2;

    // Constrain to dialog bounds
    if (left < padding) left = padding;
    if (left + previewWidth > dialogRect.width - padding) {
      left = dialogRect.width - previewWidth - padding;
    }
    if (top < padding) top = padding;
    if (top + previewHeight > dialogRect.height - padding) {
      top = dialogRect.height - previewHeight - padding;
    }

    return {
      left: `${left}px`,
      top: `${top}px`,
      width: `${previewWidth}px`,
      height: `${previewHeight}px`,
    };
  }, [hoverState]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="!max-w-[1000px] w-[90vw] p-0 gap-0 overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--background-card)] shadow-2xl"
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>

        <div ref={dialogRef} className="relative flex flex-col h-[700px] w-full">
          {/* Header */}
          <div className="flex flex-col gap-4 px-12 pt-8 pb-6">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-1">
                <h2 className="text-xl font-bold uppercase tracking-wide text-[var(--foreground)]">
                  Select {title}
                </h2>
                <p className="text-xs uppercase tracking-wider text-[var(--foreground-muted)]">
                  // {filteredOptions.length} OPTIONS AVAILABLE
                </p>
              </div>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="flex h-8 w-8 items-center justify-center rounded transition-colors text-[var(--foreground-muted)] hover:text-[var(--foreground)] hover:bg-[var(--muted)]"
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </button>
            </div>

            {/* Search input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--foreground-placeholder)]" />
              <input
                type="text"
                placeholder="Search options..."
                value={searchQuery}
                onChange={e => dispatch({ type: 'SET_SEARCH_QUERY', payload: e.target.value })}
                className="w-full h-10 pl-10 pr-4 rounded-lg bg-[var(--background-input)] border border-[var(--border)] text-[var(--foreground)] placeholder:text-[var(--foreground-placeholder)] focus:outline-none focus:border-[#D4A84B] focus:ring-1 focus:ring-[#D4A84B] transition-colors"
              />
            </div>
          </div>

          {/* Options grid */}
          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto overflow-x-hidden px-12 pb-8 bg-[var(--background-secondary)]"
          >
            <div
              className="grid gap-4"
              style={{
                gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
              }}
            >
              {filteredOptions.map(option => (
                <OptionCard
                  key={option.value}
                  option={option}
                  isSelected={selectedValue === option.value}
                  isHovered={hoverState?.option.value === option.value}
                  onSelect={() => handleSelect(option.value)}
                  onHover={element => handleHover(option, element)}
                  onHoverEnd={handleHoverEnd}
                  category={category}
                />
              ))}
            </div>
          </div>

          {/* Hover preview overlay - clickable to select */}
          {hoverState && previewThumbnailPath && (
            <button
              type="button"
              onClick={() => handleSelect(hoverState.option.value)}
              onMouseEnter={handlePreviewMouseEnter}
              onMouseLeave={handlePreviewMouseLeave}
              onWheel={handlePreviewWheel}
              className="absolute z-50 rounded-lg overflow-hidden cursor-pointer bg-white border-2 border-[#D4A84B] shadow-2xl"
              style={previewStyle}
            >
              <img
                src={previewThumbnailPath}
                alt={hoverState.option.label}
                className="h-full w-full object-cover"
                onError={() => dispatch({ type: 'SET_PREVIEW_ERROR', payload: true })}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <span
                  className="text-sm font-bold text-white uppercase tracking-wide drop-shadow-md"
                  style={{ letterSpacing: '0.5px' }}
                >
                  {hoverState.option.label}
                </span>
              </div>
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
